import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import CustomModal from "components/common/Modal";
import Select from "components/common/Select";
import DatePicker from "components/common/DatePicker";
import Button from "components/common/Button";
import CustomSnackbar, { AlertType } from "components/common/Snackbar";

import { formatStatusLabel, formatStatusValue, formatDateYYYYMMDD } from "components/Screens/CommonUtils/index";

import { updateEmployeeStatus } from "services/BambooHrTabs/about";

import { GetEmployeeDetails } from "store/actions/GetEmployeeDetails";

import { getFieldLabels } from "util/utils";
import { STATUS_OPTIONS } from "constants";

import EmploymentStatusSchema from "validations/EmploymentStatusSchema";

import "./EditEmployeeStatus.scss";

function EditEmployeeStatus({ open, closeModal, employmentStatusData, employeeId, fetchCurrentStatus }) {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: AlertType.error });
  const dispatch = useDispatch();

  const { currentStatus, timeline } = employmentStatusData || {};

  const methods = useForm({
    resolver: yupResolver(EmploymentStatusSchema),
    mode: "all",
    defaultValues: {
      status: "",
      startDate: null,
      endDate: null,
    },
  });

  useEffect(() => {
    if (currentStatus) {
      const currentStatusEntry = timeline?.find((entry) => entry.status === currentStatus) || {};
      methods.reset({
        status: currentStatus,
        startDate: currentStatusEntry?.startDate ? new Date(currentStatusEntry.startDate) : null,
        endDate: currentStatusEntry?.endDate ? new Date(currentStatusEntry.endDate) : null,
      });
    }
  }, [currentStatus, timeline, open, methods]);

  useEffect(() => {
    if (!open) return;
    const selectedStatus = methods.getValues("status");
    if (!selectedStatus || selectedStatus === currentStatus) return;

    if (selectedStatus === "Permanent") {
      const { endDate: probationEndDate } = timeline?.find((entry) => entry.status === "Probation") || {};
      methods.setValue("startDate", probationEndDate ? new Date(probationEndDate) : null, { shouldDirty: true });
      methods.setValue("endDate", null, { shouldDirty: true });
    } else if (selectedStatus === "Notice Period") {
      const today = new Date();
      const oneMonthLater = new Date(today);
      oneMonthLater.setMonth(today.getMonth() + 1);
      methods.setValue("startDate", today, { shouldDirty: true });
      methods.setValue("endDate", oneMonthLater, { shouldDirty: true });
    } else if (selectedStatus === "Probation") {
      const { startDate: probationStartDate, endDate: probationEndDate } =
        timeline?.find((entry) => entry.status === "Probation") || {};
      methods.setValue("startDate", probationStartDate ? new Date(probationStartDate) : null, { shouldDirty: true });
      methods.setValue("endDate", probationEndDate ? new Date(probationEndDate) : null, { shouldDirty: true });
    }
  }, [methods.watch("status"), open, timeline, currentStatus, methods]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, touchedFields, submitCount },
  } = methods;

  const selectedStatus = watch("status") || currentStatus;
  const startDate = watch("startDate");

  const { startLabel, endLabel } = getFieldLabels({ selectedStatus });

  const getStatusMessage = () => {
    const currentStatusText = formatStatusLabel(currentStatus);
    const selectedStatusText = formatStatusLabel(selectedStatus);

    if (!selectedStatus || selectedStatusText === currentStatusText) {
      return "You are updating dates for the current status";
    }

    return (
      <span className="para-body-m-regular">
        You are changing the status from <span className="probation-status-text">{currentStatusText}</span> to{" "}
        <span className="probation-status-text">{selectedStatusText}</span>
      </span>
    );
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const payload = {
        status: formatStatusValue(data.status),
        startDate: data.startDate ? formatDateYYYYMMDD(data.startDate) : undefined,
        ...(data.endDate !== null && data.endDate !== undefined ? { endDate: formatDateYYYYMMDD(data.endDate) } : {}),
      };
      await updateEmployeeStatus(employeeId, payload);
      dispatch(GetEmployeeDetails({ employeeId }));
      await fetchCurrentStatus();
      setLoading(false);
      closeModal();
    } catch (err) {
      setLoading(false);
      let errorMsg = "Error saving employee status.";
      if (err?.response?.data?.error) {
        const errorArr = err.response.data.error;
        if (errorArr.length > 0) {
          errorMsg = errorArr[0].description || errorArr[0].msg || errorMsg;
        }
      }
      setSnackbar({
        open: true,
        message: errorMsg,
        type: AlertType.error,
      });
      setLoading(false);
    }
  };

  return (
    <CustomModal open={open} title="Edit Employee Status" width="700px" onClose={closeModal}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="edit-status-form">
          <div className="flex flex-col gap-1 mt-15">
            <div className="status-info-text">{getStatusMessage()}</div>
            <div className="flex flex-col gap-1">
              <div className="flex gap-1">
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Employee status"
                      options={STATUS_OPTIONS}
                      formattedValue
                      error={
                        !!errors.status && (touchedFields.status || submitCount > 0) ? errors.status.message : undefined
                      }
                      value={field.value}
                      showChipForSelected={currentStatus === selectedStatus}
                      chipLabel="Current"
                    />
                  )}
                />
              </div>

              <div>
                <div className="flex gap-1">
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        value={field.value}
                        label={startLabel}
                        error={
                          !!errors.startDate && (touchedFields.startDate || submitCount > 0)
                            ? errors.startDate.message
                            : undefined
                        }
                        disabled={selectedStatus === "Probation"}
                      />
                    )}
                  />
                  {endLabel && (
                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          label={endLabel}
                          error={
                            !!errors.endDate && (touchedFields.endDate || submitCount > 0)
                              ? errors.endDate.message
                              : undefined
                          }
                          minDate={startDate}
                          value={field.value}
                        />
                      )}
                    />
                  )}
                </div>
                {selectedStatus === "Probation" && (
                  <span className="probation-helper-text">This is linked to the joining date with customer</span>
                )}
              </div>
            </div>
          </div>

          <div className="modal-action-buttons">
            <Button onClick={closeModal} label="Cancel" variant="outlined" size="large" />
            <Button isLoading={loading} type="submit" label="Save" variant="contained" size="large" />
          </div>
        </form>
      </FormProvider>
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </CustomModal>
  );
}

EditEmployeeStatus.propTypes = {
  open: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  employmentStatusData: PropTypes.shape({}),
};

EditEmployeeStatus.defaultProps = {
  employmentStatusData: {},
};

export default EditEmployeeStatus;
